import * as React from 'react'
import { useNotify } from 'shared/ui/Notify'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Box,
  Button,
  TextField,
} from '@mui/material'
import { useCallback, useState, useEffect } from 'react'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { IEditUser } from 'infrastructure/api'
import EditUserSchool from './EditUserSchool'
import { roleNameMap, reverseMap } from '../../utility/utils'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import EmailIcon from '@mui/icons-material/Email'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import QuestionMarkIcon from '@mui/icons-material/QuestionMark'
import { editUser, resetPassword } from '../../../providers/mutations'
import { useUserResource } from '../../../api/user.hooks'
import DeleteResourceButton from '../shared/DeleteResourceButton'

const revRoleNameMap = reverseMap(roleNameMap)

export function UserEdit({ id }: { id: number | string }) {
  const { userData: data, addUserRole, removeUserRole } = useUserResource(id)

  const [selectedRole, setSelectedRole] = useState('')
  const [editName, setEditName] = useState(false)
  const [editEmail, setEditEmail] = useState(false)
  const [userName, setUserName] = useState('')
  const [userEmail, setUserEmail] = useState('')

  const notify = useNotify()

  const addRole = useCallback(() => {
    addUserRole(roleNameMap[selectedRole])
  }, [addUserRole, selectedRole])

  useEffect(() => {
    if (data) {
      setUserName(data.name)
      setUserEmail(data.email)
    }
  }, [data])

  if (!data) {
    return <div>Loading...</div>
  }

  // userRoles corresponds to the values in roleNameMap, not the keys!
  const availableRoles = Object.keys(roleNameMap).filter(
    (role) => !data.roles.includes(roleNameMap[role]),
  )

  const onRoleSelected = (event: any) => {
    setSelectedRole(event.target.value)
  }

  const onChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserName(e.target.value)
  }
  const onChangeEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserEmail(e.target.value.toLocaleLowerCase().trim())
  }

  const saveName = async () => {
    if (userName.length < 2) {
      notify('Name must be greater than 2 characters', {
        type: 'error',
      })
      return
    }

    if (userName === data.name) {
      setEditName(false)
      return
    }

    const user: IEditUser = {
      id: Number(data.id),
      name: userName,
    }
    const resp = await editUser(user)

    if (resp.errors === undefined || resp.errors.length === 0) {
      data.name = userName
    } else {
      notify(resp.errors[0].message, {
        type: 'error',
      })
      return
    }
    setEditName(false)
  }

  const saveEmail = async () => {
    if (!userEmail.includes('@') || !userEmail.includes('.')) {
      notify('Invalid Email Address', {
        type: 'error',
      })
      return
    }

    if (userEmail === data.email) {
      setEditEmail(false)
      return
    }

    const user: IEditUser = {
      id: Number(data.id),
      email: userEmail,
    }
    const resp = await editUser(user)

    if (resp.errors === undefined || resp.errors.length === 0) {
      data.email = userEmail
    } else {
      notify(resp.errors[0].message, {
        type: 'error',
      })
      return
    }
    setEditEmail(false)
  }

  const sendPasswordReset = async () => {
    if (!data.email) {
      notify('User does not have an email', {
        type: 'error',
      })
      return
    }

    const resp = await resetPassword(Number(data.id))

    if (resp.errors === undefined || resp.errors.length === 0) {
      notify('Reset Password email sent', {
        type: 'info',
      })
    } else {
      notify(resp.errors[0].message, {
        type: 'error',
      })
    }
  }

  return (
    <React.Fragment>
      <Box mb="0.5em">User: {data.id}</Box>
      <Box mb="0.5em">
        Name:
        {editName ? (
          <>
            <TextField
              label="User Name"
              placeholder={userName}
              variant="outlined"
              onChange={onChangeName}
              value={userName}
              inputProps={{
                'aria-label': 'User Name',
              }}
            />
            <IconButton aria-label="save name" onClick={saveName}>
              <SaveIcon />
            </IconButton>
          </>
        ) : (
          <>
            {data.name}
            <IconButton
              aria-label="edit name"
              onClick={() => {
                setEditName(!editName)
              }}
            >
              <EditIcon />
            </IconButton>
          </>
        )}
      </Box>
      <Box mb="0.5em">
        Email:
        {editEmail ? (
          <>
            <TextField
              label="User Email"
              placeholder={userEmail}
              variant="outlined"
              onChange={onChangeEmail}
              value={userEmail}
              inputProps={{
                'aria-label': 'User Email',
              }}
            />
            <IconButton aria-label="save email" onClick={saveEmail}>
              <SaveIcon />
            </IconButton>
          </>
        ) : (
          <>
            {data.email}
            <IconButton
              aria-label="edit email"
              onClick={() => {
                setEditEmail(!editEmail)
              }}
            >
              <EditIcon />
            </IconButton>
          </>
        )}
        <Tooltip
          title="
          Use caution when editing an email,
          since it will remove this user’s ability to login with the old email"
        >
          <QuestionMarkIcon
            sx={{ height: '12px', width: '12px', marginTop: '-3px' }}
          />
        </Tooltip>
      </Box>
      <Box mb="0.5em">
        <Button
          variant="contained"
          aria-label="reset password"
          onClick={sendPasswordReset}
        >
          <EmailIcon /> Reset Password
        </Button>
        <Tooltip
          title="
              This action will trigger the Forgot Password flow as if the user triggered it.
              They will receive an email from Kiddom prompting them to reset their
              password with a link.
              Use caution when using this action on a student account"
        >
          <QuestionMarkIcon
            sx={{ height: '12px', width: '12px', marginTop: '-3px' }}
          />
        </Tooltip>
      </Box>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          Edit Roles:
        </AccordionSummary>
        <AccordionDetails>
          <List>
            {data.roles.map((role: string) => {
              return (
                <ListItem key={role} alignItems="flex-start">
                  <ListItemText primary={revRoleNameMap[role]} />
                  <Button
                    variant="contained"
                    onClick={() => removeUserRole(role)}
                  >
                    Remove Role
                  </Button>
                </ListItem>
              )
            })}
            {!!availableRoles.length && (
              <>
                <ListItem>
                  <Select
                    labelId="env-select-label"
                    id="env-select"
                    value={selectedRole}
                    onChange={onRoleSelected}
                  >
                    {availableRoles.map((e) => (
                      <MenuItem key={e} value={e}>
                        {e}
                      </MenuItem>
                    ))}
                  </Select>
                </ListItem>
                <ListItem>
                  <Button variant="contained" onClick={addRole}>
                    Add Role
                  </Button>
                </ListItem>
              </>
            )}
          </List>
        </AccordionDetails>
      </Accordion>
      <EditUserSchool userId={data.id} />
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          Delete User
        </AccordionSummary>
        <AccordionDetails>
          <DeleteResourceButton
            name={data.name}
            resource="User"
            userID={data.id}
          />
        </AccordionDetails>
      </Accordion>
    </React.Fragment>
  )
}
